import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, 
  Users, 
  CreditCard, 
  Settings, 
  Calendar, 
  Search,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  HelpCircle,
  FileText,
  ExternalLink,
  X,
  ArrowRight,
  MessageSquare,
  Globe,
  Youtube,
  Clock,
  Briefcase,
  ShieldCheck,
  Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [view, setView] = useState('home');

  // 📺 مكتبة الفيديوهات التعليمية مع دعم "وقت البدء"
  const videos = [
    {
      id: "vSH1AFyn_bs", 
      title: "تحميل وتثبيت المرجع برو على سطح المكتب",
      thumbnail: "https://img.youtube.com/vi/vSH1AFyn_bs/maxresdefault.jpg",
      description: "دليلك الشامل لتحميل وتثبيت نظام المرجع برو على جهاز الكمبيوتر الخاص بك لتحسين الإنتاجية وسهولة الوصول.",
      duration: "04:36",
      tag: "تثبيت"
    },
    {
      id: "NYlyCZMRv_M",
      title: "فتح حساب جديد وتفعيل الفترة التجريبية",
      thumbnail: "https://img.youtube.com/vi/NYlyCZMRv_M/maxresdefault.jpg",
      description: "شرح خطوة بخطوة لكيفية تسجيل مكتبك في النظام وفتح حساب جديد لبدء إدارة أعمالك القانونية رقمياً.",
      duration: "02:01",
      tag: "البداية"
    },
    {
      id: "3sVH1ZaZPoU",
      title: "دليل تسجيل الدخول الآمن وإدارة الجلسة",
      thumbnail: "https://img.youtube.com/vi/3sVH1ZaZPoU/maxresdefault.jpg",
      description: "تعرف على طريقة تسجيل الدخول الصحيحة، وتأمين حسابك، واستخدام ميزة استعادة كلمة المرور لضمان خصوصية بياناتك.",
      duration: "02:09",
      tag: "أساسيات",
      start: 41
    },
    {
      id: "2E8XLnNB32g",
      title: "استكشاف الواجهات الرئيسية ولوحة التحكم",
      thumbnail: "https://img.youtube.com/vi/2E8XLnNB32g/maxresdefault.jpg",
      description: "جولة بصرية سريعة في واجهة النظام، نتعرف فيها على كيفية الوصول للقضايا، العملاء، والتقارير المالية بضغطة زر.",
      duration: "05:59",
      tag: "واجهات"
    },
    {
      id: "aVd2Wu7cwdU",
      title: "إضافة قيد وإدارة واجهة العميل",
      thumbnail: "https://img.youtube.com/vi/aVd2Wu7cwdU/maxresdefault.jpg",
      description: "شرح معمق لكيفية إضافة القيود والتعامل مع واجهة العميل لضمان دقة التنظيم في مكتبك.",
      duration: "18:30",
      tag: "مالية"
    }
  ];

  const categories = useMemo(() => [
    {
      id: "get_started",
      title: "البداية السريعة",
      icon: <Globe />,
      color: "blue",
      description: "كل ما تحتاجه للبدء في استخدام النظام لأول مرة.",
      items: [
        { q: "كيف أقوم بتسجيل الدخول؟", a: "استخدم البريد الإلكتروني وكلمة المرور التي تم تزويدك بها من قبل مدير النظام. يمكنك تفعيل خيار 'تذكرني' لتسهيل الدخول المرة القادمة." },
        { q: "هل يحتاج النظام لتثبيت؟", a: "النظام يعمل سحابياً عبر المتصفح، ولكن يمكنك تثبيته كتطبيق سطح مكتب عبر ميزة PWA كما موضح في فيديوهات الشرح." },
        { q: "كيف أغير لغة الواجهة؟", a: "النظام مصمم حالياً ليدعم اللغة العربية بشكل كامل للواجهة القانونية والتقارير." }
      ]
    },
    {
      id: "cases",
      title: "إدارة القضايا",
      icon: <Scale />,
      color: "indigo",
      description: "تنظيم ملفات القضايا، الجلسات، والمذكرات القانونية بذكاء.",
      items: [
        { q: "كيف أصنف نوع القضية؟", a: "عند إضافة قضية، يمكنك الاختيار من قائمة الأنواع (مدني، جنائي، شرعي، تجاري.. إلخ) لسهولة الفلترة لاحقاً." },
        { q: "كيف يتم أرشفة القضايا المنتهية؟", a: "يمكنك تغيير حالة القضية إلى 'منتهية' وستنتقل تلقائياً إلى تبويب الأرشيف بعيداً عن القضايا الجارية." },
        { q: "هل يمكنني إرفاق مسحات ضوئية؟", a: "نعم، النظام يدعم رفع كافة أنواع الملفات (PDF, صور, Word) داخل قسم المستندات في كل قضية." }
      ]
    },
    {
      id: "finance",
      title: "الحسابات المالية",
      icon: <CreditCard />,
      color: "emerald",
      description: "تتبع الأتعاب، المصروفات، وإصدار السندات المالية.",
      items: [
        { q: "كيف أحسب ضريبة القيمة المضافة؟", a: "النظام يقوم تلقائياً بحساب الضرائب بناءً على النسبة المحددة في الإعدادات المالية عند إصدار أي فاتورة." },
        { q: "كيف أتابع الدفعات المتأخرة؟", a: "في لوحة التحكم الرئيسية وعبر تقارير المالية، ستجد تنبيهاً بالموكلين الذين لديهم دفعات مستحقة ولم تسدد." },
        { q: "هل يمكن تصدير الحسابات لبرامج أخرى؟", a: "نعم، يمكنك استخراج كافة التقارير المالية بصيغة Excel أو PDF لمشاركتها مع المحاسب." }
      ]
    },
    {
        id: "calendar",
        title: "التقويم والجلسات",
        icon: <Calendar />,
        color: "rose",
        description: "مزامنة الجلسات والمهام في جدول زمني موحد.",
        items: [
          { q: "كيف أحصل على جدول جلسات الأسبوع؟", a: "من تبويب التقويم، اختر عرض 'الأسبوع' وسيظهر لك كافة المواعيد المرتبة حسب اليوم والساعة." },
          { q: "كيف يتم تحديد القاعة والمحكمة؟", a: "عند إضافة سطر الجلسة، ستجد حقولاً مخصصة لاسم المحكمة، القاعة، ورقم الدائرة لتوثيق المحضر بدقة." }
        ]
    },
    {
        id: "docs",
        title: "المستندات والتقارير",
        icon: <Printer />,
        color: "amber",
        description: "استخراج كشوفات أداء المكتب، القضايا، والمستندات.",
        items: [
          { q: "كيف أطبع وكالة موكل؟", a: "يمكنك تخزين بيانات الوكالة في قسم الموكل وطباعتها مباشرة عبر النماذج الجاهزة في النظام." },
          { q: "هل التقارير قابلة للتحميل؟", a: "كافة تقارير القضايا والمالية يتم توليدها بصيغة PDF احترافية تحمل شعار مكتبك." }
        ]
    },
    {
        id: "security",
        title: "الأمان والصلاحيات",
        icon: <ShieldCheck />,
        color: "slate",
        description: "تعديل هوية المكتب، صلاحيات الموظفين، والملف الشخصي.",
        items: [
          { q: "كيف أضيف محامي جديد للمكتب؟", a: "من خلال حساب المدير (Super Admin)، توجه إلى إدارة المستخدمين وأنشئ حساباً جديداً مع تحديد صلاحية 'محامي'." },
          { q: "هل بياناتي مشفرة؟", a: "نعم، يتم استخدام بروتوكولات تشفير عالية لحماية بيانات الموكلين ومسودات القضايا الخاصة بك." }
        ]
    }
  ], []);

  // Search Logic
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    return categories.map(cat => {
      const isCatMatch = cat.title.includes(searchQuery) || cat.description.includes(searchQuery);
      const matchingItems = cat.items.filter(item => item.q.includes(searchQuery) || item.a.includes(searchQuery));
      if (isCatMatch || matchingItems.length > 0) {
        return { ...cat, items: matchingItems.length > 0 ? matchingItems : cat.items, isMatch: true };
      }
      return null;
    }).filter(Boolean);
  }, [searchQuery, categories]);

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32 font-['Cairo'] text-right selection:bg-blue-100" dir="rtl">
      {/* 🔮 PREMIUM HEADER (Mesh Gradient Style) */}
      <div className="relative pt-24 pb-48 px-6 overflow-hidden bg-[#0A192F]">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-indigo-600/20 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-bold tracking-wider text-blue-400 uppercase bg-blue-900/30 rounded-full backdrop-blur-sm">
              دليل المستخدم الذكي
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
              مركز المساعدة <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">والدعم الفني</span>
            </h1>
            
            {/* 🔍 PREMIUM SEARCH (Glow Effect) */}
            <div className="relative group max-w-2xl mx-auto">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-25 group-focus-within:opacity-50 transition duration-1000"></div>
              <div className="relative flex items-center bg-white rounded-2xl shadow-2xl overflow-hidden">
                <Search className="absolute right-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                <input 
                  type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن سؤال، ميزة، أو شرح (مثل: إضافة قضية)"
                  className="w-full py-5 pr-14 pl-6 rounded-2xl text-lg text-gray-800 outline-none border-none focus:ring-0"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-24 relative z-20">
        {/* 📑 PREMIUM TABS (Glassmorphism) */}
        <div className="flex p-1 bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 mb-16 max-w-md mx-auto">
          <button 
            onClick={() => setView('home')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all duration-300 ${view === 'home' ? 'bg-[#0A192F] text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Briefcase className="w-5 h-5" />
            أقسام النظام
          </button>
          <button 
            onClick={() => setView('videos')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all duration-300 ${view === 'videos' ? 'bg-[#0A192F] text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Youtube className="w-5 h-5" />
            شروحات الفيديو
          </button>
        </div>

        <AnimatePresence mode="wait">
          {view === 'home' ? (
            <motion.div key="home" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCategories.map((cat) => (
                <div 
                  key={cat.id}
                  className={`bg-white rounded-[2rem] p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 relative group overflow-hidden cursor-pointer ${activeCategory === cat.id ? 'ring-2 ring-blue-500/20' : ''}`}
                  onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-700" />
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-white shadow-inner rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-gray-100 text-blue-600">
                      {React.cloneElement(cat.icon, { className: "w-8 h-8" })}
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-3">{cat.title}</h3>
                    <p className="text-gray-500 leading-relaxed mb-6 h-12 line-clamp-2">{cat.description}</p>
                    
                    <AnimatePresence>
                      {activeCategory === cat.id || searchQuery.length > 0 ? (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="space-y-4 pt-6 border-t border-gray-50 mt-4">
                          {cat.items.map((item, idx) => (
                            <div key={idx} className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50">
                              <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-blue-500" /> {item.q}</h4>
                              <p className="text-gray-600 text-[0.95rem] leading-relaxed pr-6">{item.a}</p>
                            </div>
                          ))}
                        </motion.div>
                      ) : (
                        <div className="flex items-center gap-2 text-blue-600 font-bold text-sm bg-blue-50 w-max px-4 py-2 rounded-full group-hover:bg-[#0A192F] group-hover:text-white transition-all">
                          <span>عرض الأسئلة</span>
                          <ArrowRight className="w-4 h-4 rotate-180" />
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div key="videos" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
               <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <div><h2 className="text-3xl font-black text-gray-900">أحدث الشروحات</h2><p className="text-gray-500">شاهد دروس التطبيق والتعامل مع النظام خطوة بخطوة</p></div>
                <button onClick={() => window.open('https://www.youtube.com/@AlMarjeaPro', '_blank')} className="flex items-center gap-3 px-6 py-3 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-100">
                  <Youtube className="w-5 h-5" /> زيارة القناة الرسمية
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {videos.map((video) => (
                  <motion.div whileHover={{ y: -10 }} key={video.id} className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm border border-gray-100 group cursor-pointer hover:shadow-2xl transition-all duration-500" onClick={() => setSelectedVideo(video)}>
                    <div className="relative aspect-video">
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 backdrop-blur-[2px]">
                        <PlayCircle className="w-16 h-16 text-white" />
                      </div>
                      <div className="absolute bottom-4 left-4 flex gap-2">
                        <span className="px-3 py-1 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold rounded-lg">{video.duration}</span>
                        <span className="px-3 py-1 bg-blue-600 text-white text-[10px] font-bold rounded-lg uppercase">{video.tag}</span>
                      </div>
                      {video.start && <span className="absolute top-4 right-4 bg-red-600 text-white text-[10px] px-2 py-1 rounded font-bold">يبدأ من {video.start} ث</span>}
                    </div>
                    <div className="p-8"><h3 className="text-xl font-black mb-2 group-hover:text-blue-600 transition-colors">{video.title}</h3><p className="text-gray-500 text-sm line-clamp-2 leading-relaxed">{video.description}</p></div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 🛠️ PREMIUM SUPPORT SECTION */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-32 relative group">
          <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[3rem] blur-2xl opacity-10"></div>
          <div className="relative bg-[#0A192F] rounded-[3rem] p-10 md:p-16 overflow-hidden text-center md:text-right text-white">
             <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
                <div className="max-w-xl">
                  <h2 className="text-4xl font-black mb-6">هل ما زلت بحاجة للمساعدة؟</h2>
                  <p className="text-blue-100/70 text-lg mb-8 leading-relaxed font-light">فريق الدعم الفني لشركة المرجع برو متواجد لخدمتكم وتذليل كافة العقبات التقنية التي قد تواجهكم.</p>
                  <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                    <button onClick={() => navigate('/contact-support')} className="px-10 py-4 bg-white text-[#0A192F] rounded-2xl font-black hover:bg-blue-50 transition-all shadow-xl flex items-center gap-3">
                      <MessageSquare className="w-5 h-5" /> تواصل مع الدعم
                    </button>
                    <button onClick={() => window.open('https://api.whatsapp.com/send?phone=00000', '_blank')} className="px-8 py-4 bg-white/10 border border-white/20 rounded-2xl font-bold hover:bg-white/20 transition-all">واتساب مبيعات</button>
                  </div>
                </div>
                <div className="hidden lg:flex w-64 h-64 bg-blue-500/10 rounded-full items-center justify-center border border-white/5 animate-pulse">
                   <HelpCircle className="w-32 h-32 text-blue-400/20" />
                </div>
             </div>
          </div>
        </motion.div>
      </div>

      {/* 🎬 ULTRA-MODERN VIDEO MODAL */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl" onClick={() => setSelectedVideo(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative w-full max-w-6xl aspect-video rounded-[2rem] overflow-hidden shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
               <button className="absolute top-6 right-6 z-20 p-2 bg-white/10 text-white rounded-full hover:bg-white/20 backdrop-blur-md" onClick={() => setSelectedVideo(null)}>
                 <X className="w-7 h-7" />
               </button>
               <iframe 
                 width="100%" height="100%" 
                 src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1${selectedVideo.start ? `&start=${selectedVideo.start}` : ''}`}
                 title="YouTube Player" frameBorder="0" allowFullScreen
               ></iframe>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HelpCenter;
